import { postBillingAddress } from '@backmarket/http-api/src/api-specs-after-sale-experience/client/billing-address'
import { postShippingAddress } from '@backmarket/http-api/src/api-specs-after-sale-experience/client/shipping-address'
import { postBuyBackAddress } from '@backmarket/http-api/src/api-specs-buyback/customer/postAddress'
import { postUpdateUserInformation } from '@backmarket/http-api/src/api-specs-checkout/cart/cart'
import type {
  Bill,
  BillableCountry,
  CartResponse,
  Deliver,
  DeliverCollectionPoint,
  ShippableCountry,
  UserInformation,
} from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { Country } from '@backmarket/http-api/src/standards/Country'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { defineStore } from 'pinia'

import { FORM_TYPES } from '../types/form'
import { cleanAddressState } from '../utils/cleanAddressState'

interface AddressStore {
  shipping: Deliver
  billing: Bill
  collectionPoint: DeliverCollectionPoint | undefined
  shippableCountries: ShippableCountry[]
  billableCountries: BillableCountry[]
}

const addUserInformation = async ({
  address,
  formType,
  userInformation,
}: {
  address: Partial<Deliver | Bill>
  formType: (typeof FORM_TYPES)[keyof typeof FORM_TYPES]
  userInformation?: Partial<UserInformation>
}) => {
  const taxId = address.customerIdNumber ?? userInformation?.taxId

  const userInformationBody = {
    birthdate: address.birthdate ?? userInformation?.birthdate,
    ...(taxId !== undefined ? { taxId } : {}),
  }

  await $httpFetch(postUpdateUserInformation, {
    body: { ...userInformationBody, formType },
  })
}

export const useAddressStore = defineStore('addressStore', {
  state: (): AddressStore => ({
    shipping: {
      birthdate: '',
      city: '',
      company: '',
      country: '' as Country,
      countryDialInCode: '',
      customerIdNumber: '',
      dateCreation: '',
      dateModification: '',
      email: '',
      firstName: '',
      firstNamePronunciation: '',
      lastName: '',
      lastNamePronunciation: '',
      phone: '',
      postalCode: '',
      stateOrProvince: '',
      street: '',
      street2: '',
      warnings: [],
    },
    billing: {
      birthdate: '',
      city: '',
      company: '',
      country: '' as Country,
      countryDialInCode: '',
      customerIdNumber: '',
      dateCreation: '',
      dateModification: '',
      email: '',
      firstName: '',
      firstNamePronunciation: '',
      lastName: '',
      lastNamePronunciation: '',
      phone: '',
      postalCode: '',
      stateOrProvince: '',
      street: '',
      street2: '',
    },
    collectionPoint: undefined,
    shippableCountries: [],
    billableCountries: [],
  }),
  getters: {
    isShippingAddressComplete: ({ shipping = {} }) => {
      return (
        !isEmpty(shipping) &&
        !isEmpty(`${shipping.firstName} ${shipping.lastName}`.trim()) &&
        !isEmpty(shipping.street?.trim()) &&
        !isEmpty(shipping.postalCode?.trim()) &&
        !isEmpty(shipping.city?.trim()) &&
        !isEmpty(shipping.country) &&
        !isEmpty(shipping.countryDialInCode) &&
        !isEmpty(shipping.phone)
      )
    },
    isBillingAddressComplete: ({ billing = {} }) => {
      return (
        !isEmpty(billing) &&
        !isEmpty(`${billing.firstName} ${billing.lastName}`.trim()) &&
        !isEmpty(billing.street?.trim()) &&
        !isEmpty(billing.postalCode?.trim()) &&
        !isEmpty(billing.city?.trim()) &&
        !isEmpty(billing.country)
      )
    },
    hasCollectionPoint: (state) => !isEmpty(state.collectionPoint),
    isCollectionPointAddressComplete: ({ collectionPoint }) => {
      return (
        !isEmpty(collectionPoint) &&
        !isEmpty(collectionPoint.firstName) &&
        !isEmpty(collectionPoint.lastName) &&
        !isEmpty(collectionPoint.countryDialInCode) &&
        !isEmpty(collectionPoint.phone)
      )
    },
  },
  actions: {
    setAddress({
      shippableCountries,
      billableCountries,
      addressList,
    }: CartResponse) {
      this.shipping = {
        ...addressList.deliver,
        customerIdNumber: addressList.bill?.customerIdNumber ?? '',
      }

      this.billing = addressList.bill
      this.collectionPoint =
        addressList.deliverCollectionPoint as DeliverCollectionPoint
      this.shippableCountries = shippableCountries
      this.billableCountries = billableCountries
    },
    async saveAddress({
      isShipping,
      isBilling,
      formType,
      address,
      userInformation,
    }: {
      isShipping: boolean
      isBilling: boolean
      formType?: (typeof FORM_TYPES)[keyof typeof FORM_TYPES]
      address: Deliver | Bill
      userInformation?: Partial<UserInformation>
    }) {
      // 0. Clean states for addresses that doesn't have one
      const cleanedAddress = removeEmptyValuesInObject(address)

      // 1. Shipping and/or billing address
      if (isShipping && isBilling) {
        await Promise.all([
          $httpFetch(postShippingAddress, { body: cleanedAddress }),
          $httpFetch(postBillingAddress, { body: cleanedAddress }),
        ])
      } else {
        if (isShipping)
          await $httpFetch(postShippingAddress, { body: cleanedAddress })
        if (isBilling)
          await $httpFetch(postBillingAddress, { body: cleanedAddress })
      }

      // 2. User information (needed only if insurance)
      const shouldUpdateUserInfo = formType === FORM_TYPES.INSURANCE_ONLY

      if (shouldUpdateUserInfo) {
        await addUserInformation({
          address: cleanedAddress,
          formType,
          userInformation,
        })
      }
    },
    async createBuybackAddress(address: Deliver) {
      const cleanedAddress = cleanAddressState(address)

      await $httpFetch(postBuyBackAddress, {
        body: { ...cleanedAddress },
      })
    },
  },
})
